"use client";
import {
  ButtonComponent,
  DrawerComponent,
  SelectComponent,
  TextFieldComponent,
} from "@/components";
import Address from "@/components/Address";
import AutoCompleteSearch from "@/components/AutoCompleteSearch";
import {
  EMAIL_REGEX,
  EMAIL_TYPE,
  FREE_TEXT_REGEX,
  IMAGES,
  MOBILE_REGEX,
  NUMBER_REGEX,
  WEB_COLORS,
} from "@/lib/constants";
import {
  useGetEducationListQuery,
  useGetLanguagesListQuery,
  useGetMaritalStatusListQuery,
  useGetPurposeOfLoanListQuery,
} from "@/lib/redux";
import { Button, Grid, InputAdornment, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useCallback, useState } from "react";
import { WorkAddressDrawer } from "../WorkAddressDrawer";

const WorkDetailFields = ({
  appId,
  phone,
  formik,
  timeLeft,
  employmentType,
  handleVerifyEmailClick,
  handleFieldChange,
  appProps,
  sendEmailOtpError,
}) => {
  // const employmentType = "Salaried";
  const t = useTranslations("WorkDetails");
  const [openMapDrawer, setOpenMapDrawer] = useState(false);
  const { data: languageData } = useGetLanguagesListQuery();
  const { data: educationList } = useGetEducationListQuery();
  const { data: maritalStatusData } = useGetMaritalStatusListQuery();
  const { data: purposeOfLoanData } = useGetPurposeOfLoanListQuery();

  const handleDrawerClose = useCallback(() => {
    setOpenMapDrawer(false);
  }, [setOpenMapDrawer]);

  const handleOnSubmitAddress = useCallback(
    (addressData) => {
      const newFormikValues = {
        line2: addressData.line2,
        line3: addressData.line3,
        line4: addressData.line4,
        line5: addressData.line5,
        landmark: addressData.landmark,
        city: addressData.city,
        country: addressData.country,
        state: addressData.state,
        pin: addressData.pin,
      };
      if (typeof addressData?.isMapMyIndiaResult === "boolean") {
        newFormikValues.isMapMyIndiaResult = Boolean(
          addressData?.isMapMyIndiaResult,
        );
      }
      formik.setValues((i) => ({ ...i, ...newFormikValues }));
      setOpenMapDrawer(false);
    },
    [formik.setValues, setOpenMapDrawer],
  );

  // Validate and set error for personal email
  const handlePersonalEmailChange = (e) => {
    let value = e.target.value.replace(/\s/g, "").trim();
    e.target.value = value;
    formik.handleChange(e);
    formik.setFieldValue("emailVerified", false);
    if (!formik.touched.personalEmailId) {
      formik.setFieldTouched("personalEmailId", true, false);
    }
    if (value && !EMAIL_REGEX.test(value)) {
      formik.setFieldError("personalEmailId", "Invalid email");
    } else {
      formik.setFieldError("personalEmailId", undefined);
    }
  };

  // Validate and set error for official email
  const handleOfficialEmailChange = (e) => {
    let value = e.target.value.replace(/\s/g, "").trim();
    e.target.value = value;
    formik.handleChange(e);
    formik.setFieldValue("officialEmailVerified", false);
    if (!formik.touched.workEmailId) {
      formik.setFieldTouched("workEmailId", true, false);
    }
    if (value && !EMAIL_REGEX.test(value)) {
      formik.setFieldError("workEmailId", "Invalid email");
    } else {
      formik.setFieldError("workEmailId", undefined);
    }
  };
  return (
    <>
      <Grid container spacing={2} rowGap={1}>
        {employmentType && (
          <Grid item xs={12} md={6} lg={4}>
            {employmentType === "Salaried" ? (
              <AutoCompleteSearch formik={formik} />
            ) : (
              <TextFieldComponent
                allowCapitalization={true}
                required
                name="businessName"
                label={t("businessName")}
                placeholder={t("businessplaceholder")}
                maxLength={60}
                onBlur={formik.handleBlur}
                value={formik.values.businessName}
                error={Boolean(
                  formik.touched.businessName && formik.errors.businessName,
                )}
                onChange={(e) => handleFieldChange(e, FREE_TEXT_REGEX)}
                errorMessage={
                  formik.touched.businessName && formik.errors.businessName
                }
              />
            )}
          </Grid>
        )}
        {employmentType && (
          <Grid item xs={12} md={6} lg={4} xl={4}>
            <TextFieldComponent
              allowCapitalization={true}
              required
              label={
                employmentType === "Salaried"
                  ? t("companyAddress")
                  : t("businessAddress")
              }
              onClick={() => {
                // formik.setFieldValue("line4", "");
                setOpenMapDrawer(true);
              }}
              name="line4"
              onBlur={formik.handleBlur}
              value={[
                formik.values.line2,
                formik.values.line3,
                formik.values.city,
                formik.values.state,
                formik.values.pin,
              ]
                .filter((v) => v && v.trim() !== "")
                .join(", ")}
              placeholder={t("workBuildPlaceholder")}
              error={Boolean(formik.touched.line4 && formik.errors.line4)}
              errorMessage={formik.touched.line4 && formik.errors.line4}
            />
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <Image src={IMAGES.infoIcon} alt="Important Information" />
              <Typography variant="caption" color={WEB_COLORS.textFadeColor}>
                Search by your company name for better search results.
              </Typography>
            </Stack>
          </Grid>
        )}

        <WorkAddressDrawer
          handleDrawer={handleDrawerClose}
          fieldName={
            employmentType === "Salaried" ? "companyAddress" : "businessAddress"
          }
          open={openMapDrawer}
          handleOnSubmit={handleOnSubmitAddress}
        />

        <Grid item xs={12} md={6} lg={4}>
          <TextFieldComponent
            type="number"
            name="aadharMobileNumber"
            label={t("aadharMobileNumber")}
            placeholder={t("mobilePlaceholder")}
            maxLength={10}
            onBlur={formik.handleBlur}
            value={formik.values.aadharMobileNumber}
            error={Boolean(
              formik.touched.aadharMobileNumber &&
              formik.errors.aadharMobileNumber,
            )}
            onChange={(e) => handleFieldChange(e, MOBILE_REGEX)}
            errorMessage={
              formik.touched.aadharMobileNumber &&
              formik.errors.aadharMobileNumber
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <SelectComponent
            required
            name="maritalStatus"
            label={t("maritalStatus")}
            placeholder={t("maritalplaceholder")}
            onBlur={formik.handleBlur}
            defaultValue=""
            onChange={formik.handleChange}
            singleItemArray={maritalStatusData}
            value={formik.values.maritalStatus}
            error={Boolean(
              formik.touched.maritalStatus && formik.errors.maritalStatus,
            )}
            errorMessage={
              formik.touched.maritalStatus && formik.errors.maritalStatus
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <SelectComponent
            required
            name="education"
            label={t("education")}
            placeholder={t("educationPlaceholder")}
            onBlur={formik.handleBlur}
            defaultValue=""
            onChange={formik.handleChange}
            singleItemArray={educationList}
            value={formik.values.education}
            error={Boolean(formik.touched.education && formik.errors.education)}
            errorMessage={formik.touched.education && formik.errors.education}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <SelectComponent
            required
            name="purposeOfLoan"
            label={t("purposeOfLoan")}
            placeholder={t("purposeOfLoanPlaceholder")}
            onBlur={formik.handleBlur}
            defaultValue=""
            onChange={formik.handleChange}
            value={formik.values.purposeOfLoan}
            singleItemArray={purposeOfLoanData}
            error={Boolean(
              formik.touched.purposeOfLoan && formik.errors.purposeOfLoan,
            )}
            errorMessage={
              formik.touched.purposeOfLoan && formik.errors.purposeOfLoan
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <SelectComponent
            required
            name="language"
            label={t("language")}
            placeholder={t("languagePlaceholder")}
            onBlur={formik.handleBlur}
            defaultValue=""
            onChange={formik.handleChange}
            singleItemArray={languageData}
            value={formik.values.language}
            error={Boolean(formik.touched.language && formik.errors.language)}
            errorMessage={formik.touched.language && formik.errors.language}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextFieldComponent
            required
            type="number"
            name="netAnnualIncome"
            label={t("netAnnualIncome")}
            placeholder={t("incomePlaceholder")}
            minLength={5}
            maxLength={8}
            onBlur={formik.handleBlur}
            value={formik.values.netAnnualIncome}
            error={Boolean(
              formik.touched.netAnnualIncome && formik.errors.netAnnualIncome,
            )}
            onChange={(e) => handleFieldChange(e, NUMBER_REGEX)}
            errorMessage={
              formik.touched.netAnnualIncome && formik.errors.netAnnualIncome
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextFieldComponent
            required
            type="email"
            name="personalEmailId"
            label={t("personalEmailId")}
            placeholder={t("emailPlaceholder")}
            maxLength={40}
            onBlur={formik.handleBlur}
            value={formik.values.personalEmailId}
            disabled={formik.values.emailVerified}
            endAdornment={
              <InputAdornment position="end">
                {formik.values.emailVerified !== true ? (
                  <ButtonComponent
                    disabled={
                      !formik.values.personalEmailId ||
                      formik.errors.personalEmailId ||
                      formik.values.emailVerified === true
                    }
                    sx={{ textTransform: "capitalize" }}
                    onClick={() => handleVerifyEmailClick(EMAIL_TYPE.PERSONAL)}
                  >
                    {t("verifynow")}
                  </ButtonComponent>
                ) : (
                  <Image src={IMAGES.verified} alt="verified" />
                )}
              </InputAdornment>
            }
            error={Boolean(
              formik.touched.personalEmailId &&
              (formik.errors.personalEmailId || formik.errors.emailVerified),
            )}
            onChange={handlePersonalEmailChange}
            errorMessage={
              formik.touched.personalEmailId &&
              (formik.errors.personalEmailId ||
                sendEmailOtpError?.data?.data?.message ||
                formik.errors.emailVerified)
            }
          />
        </Grid>
        {employmentType === "Salaried" && (
          <Grid item xs={12} md={6} lg={4}>
            <TextFieldComponent
              disabled={formik.values.officialEmailVerified}
              type="email"
              name="workEmailId"
              label={t("workEmailId")}
              placeholder={t("workemailPlaceholder")}
              maxLength={80}
              onBlur={formik.handleBlur}
              value={formik.values.workEmailId}
              endAdornment={
                <InputAdornment position="end">
                  {formik.values.officialEmailVerified !== true ? (
                    <ButtonComponent
                      disabled={
                        !formik.values.workEmailId ||
                        formik.errors.workEmailId ||
                        formik.values.officialEmailVerified === true
                      }
                      sx={{ textTransform: "capitalize" }}
                      onClick={() => handleVerifyEmailClick(EMAIL_TYPE.WORK)}
                    >
                      {t("verifynow")}
                    </ButtonComponent>
                  ) : (
                    <Image src={IMAGES.verified} alt="verified" />
                  )}
                </InputAdornment>
              }
              error={Boolean(
                formik.values.workEmailId &&
                (sendEmailOtpError?.data?.message ||
                  (formik?.touched?.workEmailId &&
                    (formik?.errors?.workEmailId ||
                      formik?.errors?.officialEmailVerified))),
              )}
              onChange={handleOfficialEmailChange}
              errorMessage={
                sendEmailOtpError?.data?.data?.message ||
                sendEmailOtpError?.data?.message ||
                (formik?.touched?.workEmailId &&
                  (formik?.errors?.workEmailId ||
                    formik?.errors?.officialEmailVerified))
              }
            />
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <Image src={IMAGES.infoIcon} alt="Important Information" />
              <Typography variant="caption" color={WEB_COLORS.textFadeColor}>
                Note: Verifying work email helps fast-track loan approval
                process.
              </Typography>
            </Stack>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default WorkDetailFields;
